import {
    Divider,
    FormControl,
    InputLabel,
    LinearProgress,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
} from '@mui/material';
import { ENABLE_CC_CUSTOM_HEADERS, isFeatureEnabled } from '/b2b/common/helpers/FeatureFlags';

import API from '/b2b/common/helpers/API';
import { API_NAME } from '#config/runtimeconfig';
import AdminHelpers from './components/AdminHelpers';
import AuthenticatedPaths from '/b2b/routing/AuthenticatedRoutes/AuthenticatedRoutes.paths';
import { LOCALES } from '/b2b/common/helpers/i18n';
import Locale from '/b2b/common/components/Locale';
import NavLinkListItem from '/b2b/common/components/NavLinkListItem';
import { PERMISSIONS } from '/b2b/authentication/constants';
import Permission from '/b2b/common/components/Permission';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { handleError } from '/b2b/common/services/helpers';
import makeStyles from '@mui/styles/makeStyles';
import styles from './UserDetails.styles';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(styles);

export const fetchBillingPortalUrl = async () => {
    return API.get(API_NAME, '/customer/billing-portal').catch(handleError);
};

const UserDetails = props => {
    const customHeadersEnabled = useSelector(state =>
        isFeatureEnabled(state, ENABLE_CC_CUSTOM_HEADERS)
    );
    const {
        adminHelpersEnabled,
        className,
        createSnack,
        currentLocale,
        hasPermissionToViewOrganizations,
        onChangeLocale,
        onSignOut,
        onOrganizationsClick,
        shouldRenderApiKeys,
    } = props;
    const classes = useStyles(props);
    const rootClassName = classNames(classes.root, className);

    const [fetchingBillingPortalUrl, setFetchingBillingPortalUrl] = React.useState('');
    const [menuContainer, setMenuContainer] = React.useState(null);

    const handleLocaleChange = event => {
        const { value } = event.target;
        onChangeLocale && onChangeLocale(value);
        window.localStorage && window.localStorage.setItem('selectedLocale', value);
    };

    const handleOrganizationsClick = e => {
        e.preventDefault();
        return onOrganizationsClick();
    };

    const handleBillingPortalClick = e => {
        e.preventDefault();

        setFetchingBillingPortalUrl(true);

        fetchBillingPortalUrl()
            .then(({ url = '' } = {}) => window.open(url, '_blank', 'noopener,noreferrer'))
            .catch(error => {
                createSnack(error?.message);
            })
            .finally(() => {
                setFetchingBillingPortalUrl(false);
            });
    };

    return (
        <div className={rootClassName} ref={setMenuContainer}>
            {fetchingBillingPortalUrl && <LinearProgress />}
            <div className={classes.content}>
                <List component="div">
                    {adminHelpersEnabled && <AdminHelpers menuContainer={menuContainer} />}
                    <FormControl className={classes.formControl} variant="outlined" size="small">
                        <InputLabel id="user-details-locale-label">
                            <Locale path="common.language" />
                        </InputLabel>
                        <Select
                            labelId="user-details-locale-label"
                            label={<Locale path="common.language" />}
                            value={currentLocale}
                            onChange={handleLocaleChange}
                            MenuProps={{ container: menuContainer }}
                        >
                            {LOCALES.slice()
                                .sort((a, b) => (a.label > b.label ? 1 : -1))
                                .map(({ label, value }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <ListItemButton
                        activeClassName={classes.activeListItem}
                        className={classes.listItem}
                        component={NavLinkListItem}
                        to={AuthenticatedPaths.ACCOUNT}
                        isActive={(match, location) =>
                            match
                                ? true
                                : location.pathname.indexOf(AuthenticatedPaths.ACCOUNT) === 0
                        }
                    >
                        <ListItemText
                            classes={{ primary: classes.listItemText }}
                            primary={<Locale path={'common.account'} />}
                        />
                    </ListItemButton>
                    <Permission forRole toPerform="view" on={AuthenticatedPaths.USERS}>
                        <ListItemButton
                            activeClassName={classes.activeListItem}
                            className={classes.listItem}
                            component={NavLinkListItem}
                            to={AuthenticatedPaths.USERS}
                            isActive={(match, location) =>
                                match
                                    ? true
                                    : location.pathname.indexOf(`${AuthenticatedPaths.USERS}/`) ===
                                      0
                            }
                        >
                            <ListItemText
                                classes={{ primary: classes.listItemText }}
                                primary={<Locale path={'common.users'} />}
                            />
                        </ListItemButton>
                    </Permission>
                    <Permission forRole toPerform="view" on={AuthenticatedPaths.ORGANIZATIONS}>
                        <ListItemButton
                            className={
                                hasPermissionToViewOrganizations
                                    ? classes.listItem
                                    : classes.disabled
                            }
                            onClick={e =>
                                hasPermissionToViewOrganizations
                                    ? onOrganizationsClick
                                    : handleOrganizationsClick(e)
                            }
                            component={NavLinkListItem}
                            to={AuthenticatedPaths.ORGANIZATIONS}
                        >
                            <ListItemText
                                classes={{ primary: classes.listItemText }}
                                primary={<Locale path="common.fields.organizations.label" />}
                            />
                        </ListItemButton>
                    </Permission>
                    <Permission
                        toPerform={{
                            [AuthenticatedPaths.API_KEYS]: { view: true },
                            [PERMISSIONS.DATA_MAPPING]: { read: true },
                        }}
                        shouldRender={shouldRenderApiKeys}
                    >
                        <ListItemButton
                            activeClassName={classes.activeListItem}
                            className={classes.listItem}
                            component={NavLinkListItem}
                            to={AuthenticatedPaths.API_KEYS}
                            isActive={(match, location) =>
                                match
                                    ? true
                                    : location.pathname.indexOf(AuthenticatedPaths.API_KEYS) === 0
                            }
                        >
                            <ListItemText
                                classes={{ primary: classes.listItemText }}
                                primary={<Locale path="common.apiKeys" />}
                            />
                        </ListItemButton>
                    </Permission>

                    {customHeadersEnabled && (
                        <Permission toPerform={{ [PERMISSIONS.DOMAINS]: { read: true } }}>
                            <ListItemButton
                                activeClassName={classes.activeListItem}
                                className={classes.listItem}
                                component={NavLinkListItem}
                                to={AuthenticatedPaths.CUSTOM_HEADERS}
                            >
                                <ListItemText
                                    classes={{ primary: classes.listItemText }}
                                    primary={<Locale path="scanHeaders.title" />}
                                />
                            </ListItemButton>
                        </Permission>
                    )}

                    <Permission
                        toPerform={{
                            [AuthenticatedPaths.WEBHOOKS]: { view: true },
                            [PERMISSIONS.ADMIN]: { read: true },
                        }}
                    >
                        <ListItemButton
                            activeClassName={classes.activeListItem}
                            className={classes.listItem}
                            component={NavLinkListItem}
                            to={AuthenticatedPaths.WEBHOOKS}
                            isActive={(match, location) =>
                                match
                                    ? true
                                    : location.pathname.indexOf(AuthenticatedPaths.WEBHOOKS) === 0
                            }
                        >
                            <ListItemText
                                classes={{ primary: classes.listItemText }}
                                primary={<Locale path="common.webhooks" />}
                            />
                        </ListItemButton>
                    </Permission>
                    <Permission
                        toPerform={{
                            [PERMISSIONS.ADMIN]: { read: true },
                        }}
                    >
                        <ListItemButton
                            className={classes.listItem}
                            component="a"
                            onClick={handleBillingPortalClick}
                        >
                            <ListItemText
                                classes={{ primary: classes.listItemText }}
                                primary={<Locale path="common.billing" />}
                            />
                        </ListItemButton>
                    </Permission>
                    <Divider className={classes.listDivider} />
                    <ListItemButton className={classes.listItem} onClick={onSignOut}>
                        <ListItemText
                            classes={{ primary: classes.listItemText }}
                            primary={<Locale path="common.signOut" />}
                        />
                    </ListItemButton>
                </List>
            </div>
        </div>
    );
};

UserDetails.propTypes = {
    adminHelpersEnabled: PropTypes.bool,
    className: PropTypes.string,
    createSnack: PropTypes.func,
    currentLocale: PropTypes.string,
    hasPermissionToViewOrganizations: PropTypes.bool,
    onChangeLocale: PropTypes.func,
    onSignOut: PropTypes.func,
    onOrganizationsClick: PropTypes.func,
    shouldRenderApiKeys: PropTypes.bool,
};

UserDetails.defaultProps = {
    onSignOut: () => {},
};

export default UserDetails;
